import { Controller, useForm } from "react-hook-form";
import { GestaoResiduosForm, GestaoResiduosSchema } from "../../../schemas/Obras_gestao_residuos/gestaoderesiduos";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, IconButton, styled } from "@mui/material";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Props = {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: GestaoResiduosForm) => void;
  dados?: GestaoResiduosForm | null;
  editmod: boolean;
};

export const ModalCreateGestaoResiduos = ({ open, saving, onClose, onSubmit, dados, editmod }: Props) => {
  const hookForm = useForm<GestaoResiduosForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GestaoResiduosSchema),
  });
  useEffect(() => {
    if (dados) {
      hookForm.reset({
        ...dados,
      });
    } else {
      hookForm.reset({
        classe: "",
        caracteristica: "",
        tipo: "",
        formaSegregacao: "",
        empresaGerenciadora: "",
        destinacaoFinal: "",
        nomeArquivo: null,
        arquivo: undefined,
      });
    }
  }, [dados]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleSubmit = useCallback(
    (form: any) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit],
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    hookForm.setValue("nomeArquivo", file.name);
    field.onChange(file);
  };

  const handleRemoveFile = (field: any) => {
    field.onChange(undefined);
    hookForm.setValue("nomeArquivo", null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: hookForm.handleSubmit(handleSubmit),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": { width: "100%", maxWidth: "80rem" },
          },
        }}
      >
        <DialogTitle>Gestão de Resíduos</DialogTitle>
        <DialogContent>
          <ModalLoading loading={saving}>
            <ModalContainer>
              <Box display="flex" flexDirection="column" gap={2} p={3}>
                <DefaultFormInput name="classe" label="Classe" reactHookForm={hookForm} required={true} />

                <DefaultFormInput name="caracteristica" label="Característica" reactHookForm={hookForm} required={false} />

                <DefaultFormInput name="tipo" label="Tipo" reactHookForm={hookForm} required={false} />

                <DefaultFormInput name="formaSegregacao" label="Forma de Segregação" reactHookForm={hookForm} required={false} />

                <DefaultFormInput name="empresaGerenciadora" label="Empresa Gerenciadora" reactHookForm={hookForm} required={false} />

                <DefaultFormInput name="destinacaoFinal" label="Destinação Final" reactHookForm={hookForm} required={false} />
                <FormLabel>Arquivo</FormLabel>
                <Controller
                  name="arquivo"
                  control={hookForm.control}
                  render={({ field }) => (
                    <div className="">
                      {!hookForm.watch("nomeArquivo") ? (
                        <PrimaryButton component="label" variant="contained" disabled={saving}>
                          Escolher arquivo
                          <VisuallyHiddenInput type="file" onChange={(e) => handleFileChange(e, field)} />
                        </PrimaryButton>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span
                            onClick={() => {
                              if (!editmod) return;
                              const file = hookForm.getValues("arquivo");
                              if (file) {
                                const url = URL.createObjectURL(file);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = hookForm.getValues("nomeArquivo") || "arquivo";
                                link.click();
                                URL.revokeObjectURL(url);
                              }
                            }}
                            className={editmod ? "cursor-pointer" : "cursor-not-allowed"}
                          >
                            {hookForm.watch("nomeArquivo")}
                          </span>
                          <IconButton onClick={() => handleRemoveFile(field)}>
                            <DynamicIcon nameIcon="DeleteForeverRounded" size={22} className="text-red-500" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  )}
                />
              </Box>
            </ModalContainer>
          </ModalLoading>
        </DialogContent>
        <DialogActions>
          <Box className="w-full flex justify-end gap-4 p-4">
            <Box className="flex gap-1">
              <Box className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1 className="text-sm text-black dark:text-white" onClick={handleClose}>
                  Cancelar
                </h1>
              </Box>
              <Box width="120px">
                {saving ? (
                  <PrimaryButton buttonColor="primary-green">{"Salvando..."}</PrimaryButton>
                ) : (
                  <PrimaryButton type="submit" buttonColor="primary-green">
                    {"Salvar"}
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
